import styled from 'styled-components';
import { devices } from '@styles/global';

export const SidebarContainer = styled.aside<{
  'data-collapsed'?: boolean;
  'data-mobile-open'?: boolean;
}>`
  position: fixed;
  left: 0;
  top: var(--app-topbar-height);
  bottom: 0;
  width: ${props =>
    props['data-collapsed']
      ? 'var(--app-sidebar-collapsed-width)'
      : 'var(--app-sidebar-open-width)'};
  background: #1A4D3A;
  box-shadow: 2px 0 8px rgba(0, 0, 0, 0.1);
  z-index: 1190;
  display: flex;
  flex-direction: column;
  transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
  border-right: 1px solid rgba(255, 255, 255, 0.1);

  @media (max-width: ${devices.tablet}) {
    top: 0;
    width: ${props => props['data-mobile-open'] ? 'min(86vw, 280px)' : '0'};
    z-index: 1299;
    transform: translateX(${props => props['data-mobile-open'] ? '0' : '-100%'});
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), width 0.3s;
    box-shadow: ${props => props['data-mobile-open'] ? '4px 0 16px rgba(0, 0, 0, 0.2)' : 'none'};
  }
`;

export const SidebarOverlay = styled.div<{ 'data-open'?: boolean }>`
  position: fixed;
  inset: 0;
  width: 100vw;
  max-width: none !important;
  min-height: 100vh;
  min-height: 100dvh;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(2px);
  z-index: 1298;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.3s, visibility 0.3s;
  display: none;

  @media (max-width: ${devices.tablet}) {
    display: block;
  }

  &[data-open='true'] {
    opacity: 1;
    visibility: visible;
  }
`;

export const SidebarHeader = styled.div`
  padding: 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  flex-shrink: 0;

  html[data-layout-density='compact'] & {
    padding: 10px;
  }
`;

export const FarmSelector = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: var(--app-control-min-height);
  padding: 8px 10px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: var(--radius-md);
  color: white;
  cursor: pointer;
  transition: all 0.2s;
  text-align: left;

  &:hover {
    background: rgba(255, 255, 255, 0.15);
  }

  &:focus {
    outline: 2px solid rgba(255, 255, 255, 0.5);
    outline-offset: 2px;
  }
`;

export const FarmIcon = styled.div`
  font-size: 18px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const FarmName = styled.span`
  flex: 1;
  font-size: var(--font-size-sm);
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
`;

export const FarmChevron = styled.div`
  font-size: 14px;
  flex-shrink: 0;
  opacity: 0.7;
`;

export const SidebarContent = styled.div`
  flex: 1;
  padding: 12px 10px;
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
  gap: 14px;

  html[data-layout-density='compact'] & {
    padding: 10px 8px;
    gap: 12px;
  }

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 3px;

    &:hover {
      background: rgba(255, 255, 255, 0.3);
    }
  }
`;

export const NavSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const SectionTitle = styled.div`
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: rgba(255, 255, 255, 0.5);
  padding: 6px 10px 3px;
  margin-bottom: 2px;
`;

export const NavItem = styled.button<{ 'data-collapsed'?: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: var(--app-control-min-height);
  padding: 8px 10px;
  background: transparent;
  border: none;
  border-radius: 8px;
  color: rgba(255, 255, 255, 0.85);
  font-size: var(--font-size-sm);
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  width: 100%;
  text-align: left;
  position: relative;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: white;
  }

  &[data-active='true'] {
    background: rgba(255, 255, 255, 0.15);
    color: white;
    font-weight: 600;

    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 50%;
      transform: translateY(-50%);
      width: 3px;
      height: 60%;
      background: white;
      border-radius: 0 3px 3px 0;
    }
  }

  &[data-collapsed='true'] {
    justify-content: center;
    padding: 8px;

    &::before {
      display: none;
    }
  }
`;

export const NavItemIcon = styled.div<{ 'data-active'?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  transition: transform 0.2s;

  svg {
    width: 100%;
    height: 100%;
  }

  ${NavItem}[data-active='true'] & {
    transform: scale(1.1);
  }
`;

export const NavItemLabel = styled.span`
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const SidebarFooter = styled.div`
  padding: 12px 10px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex-shrink: 0;

  html[data-layout-density='compact'] & {
    padding: 10px 8px;
  }
`;

export const CollapseButton = styled.button<{ 'data-collapsed'?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: var(--app-control-min-height);
  padding: 8px;
  background: rgba(255, 255, 255, 0.1);
  border: none;
  border-radius: 8px;
  color: white;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 16px;

  &:hover {
    background: rgba(255, 255, 255, 0.15);
  }

  &:focus {
    outline: 2px solid rgba(255, 255, 255, 0.5);
    outline-offset: 2px;
  }

  @media (max-width: ${devices.tablet}) {
    display: none;
  }
`;
